import React, {
  useEffect,
  useState,
  useCallback,
} from "react";

import { Navigate } from "react-router-dom";

import Auth from "../../utils/AuthUtils";
import DashboardApi from "../../api/DashboardApi";

import Card from "./Card";
import DeveloperCard from "./DeveloperCard";
import TesterCard from "./TesterCard";
import SalesCard from "./SalesCard";

import RecentlyCreatedDealsChart from "./RecentlyCreatedDealsChart";
import LastChart from "./LastChart";
import DealsYearChart from "./DealsYearChart";

import { can } from "../../utils/permissionUtils";


const Loader = () => (
  <div className="p-5 text-center">
    <h4>Loading Dashboard...</h4>
  </div>
);


function Dashboard() {

  const user =
    Auth.getUserDetails();

  const permissions =
    user?.permissions;

  const nameToDisplay =
    user?.name || "User";

  const roleToDisplay =
    user?.role || "Member";


  const [dashboardData, setDashboardData] =
    useState(null);

  const [loading, setLoading] =
    useState(true);


  /* =========================================
     PERMISSION
  ========================================= */

  const hasAccess =
    can(
      permissions,
      "read",
      "Dashboard"
    );


  /* =========================================
     ROLE DETECTION
  ========================================= */

  const userRole =
    user?.role
      ?.toLowerCase()
      ?.trim();


  const isDeveloper =
    userRole === "developer";


  const isTester =
    userRole === "tester";


  const isSales =
    userRole === "sales";


  /* =========================================
     FETCH DASHBOARD
  ========================================= */

  const fetchAllData =
    useCallback(
      async () => {

        try {

          setLoading(true);


          /* =========================================
             TESTER DASHBOARD
          ========================================= */

       if (isTester) {
  const res =
    await DashboardApi.getTesterDashboard();

  console.log(
    "Tester Dashboard Response:",
    res
  );

  if (
    res?.success ||
    res?.code === 200
  ) {
    const testerData =
      res?.data || {};

    const stats =
      testerData?.stats || {};

    setDashboardData({
      stats: {
        assignedProjects:
          stats?.assignedProjects || 0,

        inTestingProjects:
          stats?.inTestingProjects || 0,

        approvedProjects:
          stats?.approvedProjects || 0,

        totalProjects:
          stats?.totalProjects ||
          stats?.assignedProjects ||
          0,

        openBugs:
          stats?.openBugs ||
          stats?.bugsFound ||
          0,

        fixedBugs:
          stats?.fixedBugs || 0,

        closedBugs:
          stats?.closedBugs || 0,

        totalBugs:
          stats?.totalBugs ||
          stats?.bugsFound ||
          0,
      },

      recentProjects:
        testerData?.recentProjects ||
        testerData?.recentTestingProjects ||
        [],

      recentBugs:
        testerData?.recentBugs || [],

      weeklyTrend:
        testerData?.weeklyTestingTrend || [],

      monthlyTrend:
        testerData?.monthlyTestingTrend || [],

      dispositionChart:
        testerData?.testerDispositionChart || [],

      followups: [],

      paymentStats: {},
    });
  }

  return;
}


          /* =========================================
             NORMAL DASHBOARD

             SUPERADMIN
             DEVELOPER
             SALES
             OTHER ROLES
          ========================================= */

          const res =
            await DashboardApi
              .getDashboardStats();


          console.log(
            "Dashboard Response:",
            res
          );


          if (res?.success) {

            setDashboardData(
              res?.data
            );

          }


        } catch (err) {

          console.error(
            "Dashboard Error:",
            err
          );


          setDashboardData({

            stats: {},

            recentProjects: [],

            recentBugs: [],

            weeklyTrend: [],

            monthlyTrend: [],

            dispositionChart: [],

            followups: [],

            paymentStats: {},

          });


        } finally {

          setLoading(false);

        }

      },

      [
        isTester,
      ]

    );


  /* =========================================
     INITIAL LOAD
  ========================================= */

  useEffect(() => {

    if (hasAccess) {

      fetchAllData();

    }

  }, [
    fetchAllData,
    hasAccess,
  ]);


  /* =========================================
     NO ACCESS
  ========================================= */

  if (!hasAccess) {

    return (

      <Navigate
        to="/403"
        replace
      />

    );

  }


  /* =========================================
     LOADING
  ========================================= */

  if (loading) {

    return <Loader />;

  }


  return (

    <div className="content modern-dashboard">


      {/* =========================================
         WELCOME SECTION
      ========================================= */}

      <div className="welcome-wrap mb-4">

        <div
          className="
            d-flex
            justify-content-between
            align-items-center
            flex-wrap
            gap-3
            bg-dark
            rounded
            p-4
          "
        >

          <div>

            <h2 className="text-white mb-1">

              Welcome Back,
              {" "}
              {nameToDisplay}

            </h2>


            <p className="text-light mb-0">

              Logged in as

              <span
                className="
                  badge
                  bg-primary
                  ms-2
                  text-uppercase
                "
              >

                {roleToDisplay}

              </span>

            </p>

          </div>


          <button
            className="btn btn-light"
            onClick={fetchAllData}
            disabled={loading}
          >

            {loading ? (

              <>

                <span
                  className="
                    spinner-border
                    spinner-border-sm
                    me-2
                  "
                />

                Refreshing...

              </>

            ) : (

              <>

                <i className="ti ti-refresh me-2" />

                Refresh

              </>

            )}

          </button>

        </div>

      </div>


      {/* =========================================
         ROLE BASED CARDS
      ========================================= */}

      {isDeveloper ? (

        <DeveloperCard
          stats={
            dashboardData?.stats
          }
        />

      ) : isTester ? (

        <TesterCard
          stats={
            dashboardData?.stats
          }
        />

      ) : isSales ? (

        <SalesCard
          stats={
            dashboardData?.stats
          }

          followups={
            dashboardData?.followups
          }
        />

      ) : (

        /* =========================================
           SUPER ADMIN / NORMAL DASHBOARD

           YEHI TUMHARA PURANA DASHBOARD HAI
        ========================================= */

        <Card

          stats={{

            ...dashboardData?.stats,


            totalRevenue:
              dashboardData
                ?.paymentStats
                ?.totalRevenue,


            totalPayments:
              dashboardData
                ?.paymentStats
                ?.totalPayments,


            paidPayments:
              dashboardData
                ?.paymentStats
                ?.paidPayments,


            pendingPayments:
              dashboardData
                ?.paymentStats
                ?.pendingPayments,

          }}

        />

      )}


      {/* =========================================
         SALES EXTRA CARDS
      ========================================= */}

      {isSales && (

        <div className="row g-3 mb-4">


          {/* TODAY FOLLOWUPS */}

          <div className="col-xl-3 col-md-6">

            <div
              className="
                card
                shadow-sm
                border-0
                h-100
              "
            >

              <div className="card-body">

                <h6 className="text-muted">

                  Today's Followups

                </h6>


                <h2>

                  {
                    dashboardData
                      ?.stats
                      ?.todayFollowups || 0
                  }

                </h2>

              </div>

            </div>

          </div>


          {/* PENDING DOCUMENTS */}

          <div className="col-xl-3 col-md-6">

            <div
              className="
                card
                shadow-sm
                border-0
                h-100
              "
            >

              <div className="card-body">

                <h6 className="text-muted">

                  Pending Documents

                </h6>


                <h2>

                  {
                    dashboardData
                      ?.stats
                      ?.pendingDocuments || 0
                  }

                </h2>

              </div>

            </div>

          </div>


          {/* OVERDUE FOLLOWUPS */}

          <div className="col-xl-3 col-md-6">

            <div
              className="
                card
                shadow-sm
                border-0
                h-100
              "
              style={{
                cursor: "pointer",
              }}
              onClick={() => {

                window.dispatchEvent(

                  new CustomEvent(
                    "dashboard-overdue-followups"
                  )

                );


                window.scrollTo({

                  top: 0,

                  behavior: "smooth",

                });

              }}
            >

              <div className="card-body">

                <h6 className="text-muted">

                  Overdue Followups

                </h6>


                <h2>

                  {
                    dashboardData
                      ?.stats
                      ?.overdueFollowups || 0
                  }

                </h2>


                <small className="text-danger">

                  Click to view overdue followups

                </small>

              </div>

            </div>

          </div>


          {/* TODAY CALLBACKS */}

          <div className="col-xl-3 col-md-6">

            <div
              className="
                card
                shadow-sm
                border-0
                h-100
              "
            >

              <div className="card-body">

                <h6 className="text-muted">

                  Today's Callbacks

                </h6>


                <h2>

                  {
                    dashboardData
                      ?.stats
                      ?.todayCallbacks || 0
                  }

                </h2>

              </div>

            </div>

          </div>

        </div>

      )}


      {/* =========================================
         TESTER CONTENT
      ========================================= */}

      {isTester && (

        <div className="row g-4 mb-4">


          {/* RECENT PROJECTS */}

          <div className="col-lg-7">

            <div className="card h-100">

              <div className="card-header">

                <h4 className="mb-0">

                  Recent Testing Projects

                </h4>

              </div>


              <div className="card-body">

                <div className="table-responsive">

                  <table
                    className="
                      table
                      table-hover
                      align-middle
                      mb-0
                    "
                  >

                    <thead>

                      <tr>

                        <th>
                          Project
                        </th>

                        <th>
                          Developer
                        </th>

                        <th>
                          Status
                        </th>

                        <th>
                          Bugs
                        </th>

                      </tr>

                    </thead>


                    <tbody>

                      {
                        dashboardData
                          ?.recentProjects
                          ?.length > 0 ? (

                          dashboardData
                            .recentProjects
                            .map(
                              (project) => (

                                <tr
                                  key={
                                    project?._id
                                  }
                                >

                                  <td>

                                    <strong>

                                      {
                                        project
                                          ?.projectName ||
                                        "-"
                                      }

                                    </strong>

                                  </td>


                                  <td>

                                    {
                                      project
                                        ?.assignedDeveloper
                                        ?.name ||
                                      "-"
                                    }

                                  </td>


                                  <td>

                                    <span className="badge bg-info">

                                      {
                                        project
                                          ?.testingStatus ||
                                        "-"
                                      }

                                    </span>

                                  </td>


                                  <td>

                                    {
                                      project
                                        ?.bugCount || 0
                                    }

                                  </td>

                                </tr>

                              )

                            )

                        ) : (

                          <tr>

                            <td
                              colSpan="4"
                              className="
                                text-center
                                py-4
                              "
                            >

                              No testing projects found

                            </td>

                          </tr>

                        )

                      }

                    </tbody>

                  </table>

                </div>

              </div>

            </div>

          </div>


          {/* RECENT BUGS */}

          <div className="col-lg-5">

            <div className="card h-100">

              <div className="card-header">

                <h4 className="mb-0">

                  Recent Bugs

                </h4>

              </div>


              <div className="card-body">

                <div className="table-responsive">

                  <table
                    className="
                      table
                      table-hover
                      align-middle
                      mb-0
                    "
                  >

                    <thead>

                      <tr>

                        <th>
                          Bug
                        </th>

                        <th>
                          Project
                        </th>

                        <th>
                          Status
                        </th>

                      </tr>

                    </thead>


                    <tbody>

                      {
                        dashboardData
                          ?.recentBugs
                          ?.length > 0 ? (

                          dashboardData
                            .recentBugs
                            .map(
                              (bug) => (

                                <tr
                                  key={
                                    bug?._id
                                  }
                                >

                                  <td>

                                    {
                                      bug?.title ||
                                      "-"
                                    }

                                  </td>


                                  <td>

                                    {
                                      bug
                                        ?.projectId
                                        ?.projectName ||
                                      "-"
                                    }

                                  </td>


                                  <td>

                                    <span
                                      className={
                                        bug?.status === "closed"
                                          ? "badge bg-success"
                                          : bug?.status === "fixed"
                                            ? "badge bg-warning"
                                            : "badge bg-danger"
                                      }
                                    >

                                      {
                                        bug?.status ||
                                        "-"
                                      }

                                    </span>

                                  </td>

                                </tr>

                              )

                            )

                        ) : (

                          <tr>

                            <td
                              colSpan="3"
                              className="
                                text-center
                                py-4
                              "
                            >

                              No bugs found

                            </td>

                          </tr>

                        )

                      }

                    </tbody>

                  </table>

                </div>

              </div>

            </div>

          </div>

        </div>

      )}


      {/* =========================================
         CHARTS

         TESTER KE LIYE ABHI HIDE
         BAAKI SABKE LIYE SHOW
      ========================================= */}

      {!isTester && (

        <>


          <div className="row">


            <div className="col-md-7 d-flex">

              <div className="card flex-fill">

                <RecentlyCreatedDealsChart

                  data={
                    dashboardData
                      ?.weeklyTrend || []
                  }

                  title={
                    isDeveloper
                      ? "Weekly Project Creation"
                      : isSales
                        ? "Weekly Followups"
                        : "Weekly Lead Generation"
                  }

                />

              </div>

            </div>


            <div className="col-md-5 d-flex">

              <div className="card flex-fill">

                <LastChart

                  data={
                    dashboardData
                      ?.dispositionChart || []
                  }

                  title={
                    isDeveloper
                      ? "Projects By Status"
                      : isSales
                        ? "Followup Status"
                        : "Leads By Status"
                  }

                />

              </div>

            </div>

          </div>


          <div className="row mt-4">

            <div className="col-md-12 d-flex">

              <div className="card w-100">


                <div className="card-header">

                  <h4>

                    Monthly Overview

                  </h4>

                </div>


                <div className="card-body">

                  <DealsYearChart

                    data={
                      dashboardData
                        ?.monthlyTrend || []
                    }

                    title={
                      isDeveloper
                        ? "Projects Created Per Month"
                        : isSales
                          ? "Monthly Followups"
                          : "Monthly Lead Overview"
                    }

                  />

                </div>

              </div>

            </div>

          </div>

        </>

      )}


    </div>

  );

}


export default Dashboard;